import React, { useMemo } from 'react';
import { ThemeProvider } from 'next-themes';

export interface State {
  userData: string;
  firebaseToken: string;
  firebaseUid: string;
  showPageLoader: boolean;
  unreadMsgCount: string;
  closeClientDetailNote: boolean;
  viewMode: string;
  userRole: string;
  activeProviders: Array<Object>;
  refreshPendingNote: boolean;
  refreshClientDetails: boolean;
  unreadTaskNotificationCount: string;
}

const initialState = {
  userData: "",
  firebaseToken: "",
  firebaseUid: "",
  showPageLoader: false,
  unreadMsgCount: "",
  closeClientDetailNote: false,
  viewMode: "provider",
  userRole: "provider",
  updateOnBoardStatus: false,
  activeProviders: [],
  providerFilters: "",
  selectedProviderId: "",
  refreshPendingNote: false,
  refreshClientDetails: false,
  unreadTaskNotificationCount: ""
};

type Action =
  | {
      type: "USER_DATA";
      data: {};
    }
  | {
      type: "FIREBASE_TOKEN";
      data: { idtoken: ""; firebaseUid: "" };
    }
  | {
      type: "PAGE_LOADER";
      value: false;
    }
  | {
      type: "UNREAD_MSG_COUNT";
      value: "";
    }
  | {
      type: "CLOSE_CLIENT_DETAIL_NOTE";
      value: false;
    }
  | {
      type: "CHANGE_VIEW_MODE";
      value: "provider";
    }
  | {
      type: "CHANGE_USER_ROLE";
      value: "provider";
    }
  | {
      type: "UPDATE_ONBOARD_STATUS";
      value: false;
    }
  | {
      type: "ACTIVE_PROVIDERS";
      value: [];
    }
  | {
      type: "PROVIDER_FILTERS";
      value: {};
    }
  | {
      type: "SELECTED_PROVIDER_ID";
      value: "";
    }
  | {
      type: "REFRESH_PENDING_NOTE";
      value: false;
    }
  | {
      type: "SET_REFRESH_CLIENT_DETAILS";
      value: false;
    }
  | {
      type: "UNREAD_TASK_NOTIFICATION_COUNT";
      value: ""
    };

export const UIContext = React.createContext<State | any>(initialState);

UIContext.displayName = 'UIContext';

function uiReducer(state: State, action: Action) {
  switch (action.type) {
    case 'USER_DATA': {
      return {
        ...state,
        userData: action.data
      };
    }
    case 'FIREBASE_TOKEN': {
      return {
        ...state,
        firebaseToken: action.data.idtoken,
        firebaseUid: action.data.firebaseUid
      };
    }
    case 'PAGE_LOADER': {
      return {
        ...state,
        showPageLoader: action.value,
      };
    }
    case 'UNREAD_MSG_COUNT': {
      return {
        ...state,
        unreadMsgCount: action.value,
      };
    }
    case 'CLOSE_CLIENT_DETAIL_NOTE': {
      return {
        ...state,
        closeClientDetailNote: action.value
      };
    }
    case 'CHANGE_VIEW_MODE': {
      return {
        ...state,
        viewMode: action.value
      };
    }
    case 'CHANGE_USER_ROLE': {
      return {
        ...state,
        userRole: action.value
      };
    }
    case 'UPDATE_ONBOARD_STATUS': {
      return {
        ...state,
        updateOnBoardStatus: action.value
      };
    }
    case 'ACTIVE_PROVIDERS': {
      return {
        ...state,
        activeProviders: action.value
      };
    }
    case 'PROVIDER_FILTERS': {
      return {
        ...state,
        providerFilters: action.value
      };
    }
    case 'SELECTED_PROVIDER_ID': {
      return {
        ...state,
        selectedProviderId: action.value
      };
    }
    case 'REFRESH_PENDING_NOTE': {
      return {
        ...state,
        refreshPendingNote: action.value
      };
    }
    case "SET_REFRESH_CLIENT_DETAILS": {
      return {
        ...state,
        refreshClientDetails: action.value
      };
    }
    case 'UNREAD_TASK_NOTIFICATION_COUNT': {
      return {
        ...state,
        unreadTaskNotificationCount: action.value,
      };
    }

  }
}

export const UIProvider = (props) => {
  const [state, dispatch] = React.useReducer(uiReducer, initialState);

  const setUserData = (data) => {
    dispatch({ type: 'USER_DATA', data });
  };

  const setFirebaseToken = (data) => {
    dispatch({ type: 'FIREBASE_TOKEN', data });
  };

  const setShowPageLoader = (value) => {
    dispatch({ type: 'PAGE_LOADER', value });
  };

  const setUnreadMsgCount = (value) => {
    dispatch({ type: 'UNREAD_MSG_COUNT', value });
  };

  const setCloseClientDetailNote = (value) => {
    dispatch({ type: 'CLOSE_CLIENT_DETAIL_NOTE', value});
  };

  const setViewMode = (value) => {
    dispatch({type: 'CHANGE_VIEW_MODE', value});
  };

  const setUserRole = (value) => {
    dispatch({type: 'CHANGE_USER_ROLE', value});
  };

  const setUpdateOnBoardStatus = (value) => {
    dispatch({type: 'UPDATE_ONBOARD_STATUS', value});
  };

  const setActiveProviders = (value) => {
    dispatch({type: 'ACTIVE_PROVIDERS', value});
  };

  const setProviderFilters = (value) => {
    dispatch({type: 'PROVIDER_FILTERS', value});
  };

  const setSelectedProviderId = (value) => {
    dispatch({type: 'SELECTED_PROVIDER_ID', value});
  };

  const setRefreshingNote = (value) => {
    dispatch({type: 'REFRESH_PENDING_NOTE', value});
  };

  const setRefreshingClientDetails = (value) => {
    	dispatch({ type: "SET_REFRESH_CLIENT_DETAILS", value });
  	};

  const setUnreadTaskNotificationCount = (value) => {
    dispatch({ type: 'UNREAD_TASK_NOTIFICATION_COUNT', value });
  };  

  const value = useMemo(
    () => ({
      ...state,
      setUserData,
      setFirebaseToken,
      setShowPageLoader,
      setUnreadMsgCount,
      setCloseClientDetailNote,
      setViewMode,
      setUserRole,
      setUpdateOnBoardStatus,
      setActiveProviders,
      setProviderFilters,
      setSelectedProviderId,
      setRefreshingNote,
      setRefreshingClientDetails,
      setUnreadTaskNotificationCount
    }),
    [state]
  );

  return <UIContext.Provider value={value} {...props} />;
};

export const useUI = () => {
  const context = React.useContext(UIContext);
  if (context === undefined) {
    throw new Error(`useUI must be used within a UIProvider`);
  }
  return context;
};

export const ManagedUIContext = ({ children }) => (
  <UIProvider>
    <ThemeProvider>{children}</ThemeProvider>
  </UIProvider>
);